import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Sparkles, X, Send, Loader2, AlertTriangle, Check, Download, ChevronDown, ChevronRight,
  History, SquarePen, Search, Trash2, Pencil, ArrowLeft, Paperclip, FileText,
} from "lucide-react";
import api from "../../lib/api";
import { useEntityStore } from "../../store";

/**
 * Finance Agent assistant (port of the Finance Agent panel): a chat panel on every
 * page. It reads anything in the app
 * and proposes changes; every change shows a Confirm card and runs only when
 * the user presses Confirm (server side, from the stored request).
 *
 * The Anthropic transcript is sent back each turn. After every turn the panel
 * saves the chat to /assistant/conversations so the history view can list and
 * search past chats (private to each user); localStorage only caches the open
 * one. The server keeps pending actions and an audit trail.
 */

const STORE_KEY = "fa_assistant_v1";
// Opening the panel after this long without chat activity starts a new chat
// (the old one stays in the history list).
export const IDLE_NEW_CHAT_MS = 2 * 60 * 60 * 1000;

// Attachments: one request per file (the proxy caps a request at 25 MB).
const MAX_FILE_BYTES = 20 * 1024 * 1024;
const MAX_FILES = 10;
const ACCEPT = "image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.heic,.mp4,.mov";

// Blob URLs of uploaded files, so thumbnails are fetched once per session.
const thumbCache = new Map();

async function uploadOne(file) {
  const fd = new FormData();
  fd.append("files", file, file.name || "pasted-image.png");
  const res = await api.post("/assistant/uploads", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 180000,
  });
  const up = res.data?.uploads?.[0];
  if (!up) throw new Error(res.data?.errors?.[0] || `Could not upload ${file.name}`);
  return up;
}

const SUGGESTIONS = [
  "What is still unreconciled on the Wise BR Ltd account this month?",
  "Show the intercompany balances between all entities",
  "Why is this invoice not matched? Find the bank payment",
  "What did the tax reserve transfers move this week?",
];

// Finance Agent records have no house id prefixes (unlike ENQ-/INV- in Finance Agent).
const ID_LINKS = [];

function escapeHtml(s) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

// Small markdown: paragraphs, - lists, 1. lists, **bold**, `code`, [text](url), ids as links.
function renderMarkdown(text) {
  const inline = (s) => {
    let out = escapeHtml(s);
    out = out.replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer">$1</a>');
    out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    out = out.replace(/`([^`]+)`/g, "<code>$1</code>");
    ID_LINKS.forEach(([re, href]) => {
      out = out.replace(re, (m, id) => `<a data-internal="${href(id)}" href="${href(id)}">${id}</a>`);
    });
    return out;
  };
  const lines = (text || "").split("\n");
  const html = [];
  let list = null;
  const closeList = () => { if (list) { html.push(`</${list}>`); list = null; } };
  lines.forEach((raw) => {
    const line = raw.replace(/\s+$/, "");
    const ul = line.match(/^\s*[-*]\s+(.*)$/);
    const ol = line.match(/^\s*\d+[.)]\s+(.*)$/);
    const h = line.match(/^#{1,4}\s+(.*)$/);
    if (ul || ol) {
      const tag = ul ? "ul" : "ol";
      if (list !== tag) { closeList(); html.push(`<${tag}>`); list = tag; }
      html.push(`<li>${inline((ul || ol)[1])}</li>`);
    } else if (h) {
      closeList();
      html.push(`<p><strong>${inline(h[1])}</strong></p>`);
    } else if (!line.trim()) {
      closeList();
    } else {
      closeList();
      html.push(`<p>${inline(line)}</p>`);
    }
  });
  closeList();
  // Safe without a sanitizer: every piece of text was escaped by inline()
  // before the few tags above were added, and links are http(s) only.
  return html.join("");
}

function pageContext(pathname, entity, scope) {
  const ctx = { page: pathname };
  if (scope) ctx.scope = scope;
  if (entity) ctx.selected_entity = typeof entity === "string" ? entity : (entity.name || entity.id || "");
  return ctx;
}

const TOOL_LABELS = {
  find_endpoints: "looked up how to do it",
  api_get: "read",
  api_write: "prepared a change",
  db_query: "searched the data",
  share_file: "attached a file",
  attach_files: "prepared an upload",
  remember: "saved a note",
  forget: "removed a note",
  report_issue: "reported an issue",
};

function newConversationId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return `chat-${crypto.randomUUID()}`;
  return `chat-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function load() {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export default function AssistantPanel() {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedEntity, selectedScope } = useEntityStore();
  const saved = useMemo(load, []);
  const [open, setOpen] = useState(false);
  const [transcript, setTranscript] = useState(saved?.transcript || []);
  const [items, setItems] = useState(saved?.items || []);
  const [pending, setPending] = useState(saved?.pending || null);
  const [conversationId, setConversationId] = useState(saved?.conversationId || newConversationId());
  const [title, setTitle] = useState(saved?.title || "");
  const [view, setView] = useState("chat");
  // Only save when this tab changed the chat (opening an old one must not bump
  // it to the top). A chat cached before conversations existed is saved once.
  const dirtyRef = useRef(!saved?.conversationId && (saved?.items || []).length > 0);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [attachments, setAttachments] = useState([]); // [{key, file, previewUrl}]
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const lastActiveRef = useRef(saved?.lastActiveAt || 0);

  useEffect(() => {
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify({
        conversationId, title, transcript: transcript.slice(-80), items: items.slice(-80), pending,
        lastActiveAt: lastActiveRef.current,
      }));
    } catch { /* storage full: keep going in memory */ }
  }, [conversationId, title, transcript, items, pending]);

  // Save the open chat to the server shortly after each change, so it shows
  // up in the history list (and on other devices).
  useEffect(() => {
    if (busy || items.length === 0 || !dirtyRef.current) return undefined;
    const t = setTimeout(async () => {
      dirtyRef.current = false;
      try {
        const res = await api.put(`/assistant/conversations/${conversationId}`, { transcript, items, pending });
        if (res.data?.title) setTitle(res.data.title);
      } catch (e) {
        dirtyRef.current = true;
        console.warn("assistant: could not save conversation", e?.message);
      }
    }, 700);
    return () => clearTimeout(t);
  }, [conversationId, transcript, items, pending, busy]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [items, busy, open, pending]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "j") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  const addItems = (newOnes) => {
    dirtyRef.current = true;
    lastActiveRef.current = Date.now();
    setItems((prev) => [...prev, ...newOnes]);
  };

  const handleResult = (data, startLen) => {
    const msgs = data.messages || [];
    const fresh = msgs.slice(startLen);
    const texts = [];
    fresh.forEach((m) => {
      if (m.role !== "assistant" || !Array.isArray(m.content)) return;
      m.content.forEach((b) => { if (b.type === "text" && b.text.trim()) texts.push(b.text); });
    });
    const out = [];
    if (data.executed) {
      out.push({ kind: "executed", ...data.executed });
    }
    if (data.steps?.length) out.push({ kind: "steps", steps: data.steps });
    if (texts.length) out.push({ kind: "assistant", text: texts.join("\n\n") });
    if (data.files?.length) out.push({ kind: "files", files: data.files });
    addItems(out);
    setTranscript(msgs);
    setPending(data.pending || null);
    if (data.executed?.ok) window.dispatchEvent(new CustomEvent("fa-assistant-executed", { detail: data.executed }));
  };

  const call = async (payload, startLen) => {
    setBusy(true);
    try {
      // The turn runs as a server job and is polled: a long turn (several
      // reads) would otherwise outlive the ~100s edge timeout in front of prod.
      const start = await api.post("/assistant/chat", payload, { timeout: 60000 });
      let data = start.data;
      const deadline = Date.now() + 8 * 60 * 1000;
      while (data?.job_id && data.status === "running") {
        if (Date.now() > deadline) throw new Error("The assistant is taking too long. Try again.");
        await new Promise((r) => setTimeout(r, 1500));
        try {
          data = (await api.get(`/assistant/chat/jobs/${data.job_id}`, { timeout: 30000 })).data;
        } catch (pollErr) {
          if (pollErr?.response?.status === 404) throw pollErr;
          // transient network error: keep polling
        }
      }
      if (data?.status === "error") throw new Error(data.error || "Something went wrong");
      handleResult(data?.result || data, startLen);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Something went wrong";
      addItems([{ kind: "error", text: typeof detail === "string" ? detail : JSON.stringify(detail) }]);
    } finally {
      setBusy(false);
    }
  };

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    const tooBig = files.filter((f) => f.size > MAX_FILE_BYTES);
    if (tooBig.length) {
      addItems([{ kind: "error", text: `Too large (max 20 MB): ${tooBig.map((f) => f.name).join(", ")}` }]);
    }
    const ok = files.filter((f) => f.size <= MAX_FILE_BYTES);
    setAttachments((prev) => {
      const room = Math.max(0, MAX_FILES - prev.length);
      const added = ok.slice(0, room).map((file) => ({
        key: `${file.name}-${file.size}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        previewUrl: file.type?.startsWith("image/") ? URL.createObjectURL(file) : null,
      }));
      return [...prev, ...added];
    });
  };

  const removeAttachment = (key) => {
    setAttachments((prev) => {
      const gone = prev.find((a) => a.key === key);
      if (gone?.previewUrl) URL.revokeObjectURL(gone.previewUrl);
      return prev.filter((a) => a.key !== key);
    });
  };

  const onPaste = (e) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (files.length) {
      e.preventDefault();
      addFiles(files);
    }
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    const toUpload = text === undefined ? attachments : [];
    if ((!msg && !toUpload.length) || busy) return;
    let uploads = [];
    if (toUpload.length) {
      setBusy(true);
      try {
        for (const a of toUpload) {
          const up = await uploadOne(a.file);
          if (a.previewUrl) thumbCache.set(up.upload_id, a.previewUrl);
          uploads.push(up);
        }
      } catch (e) {
        const detail = e?.response?.data?.detail || e.message || "Upload failed";
        addItems([{ kind: "error", text: typeof detail === "string" ? detail : JSON.stringify(detail) }]);
        setBusy(false);
        return; // keep the text and the files so the user can retry
      }
      setBusy(false);
      setAttachments([]);
    }
    setInput("");
    if (pending) setPending(null); // typing instead of confirming = not confirmed (server closes it)
    const refs = uploads.map((u) => ({
      upload_id: u.upload_id, filename: u.filename, content_type: u.content_type, size_bytes: u.size_bytes,
    }));
    addItems([{ kind: "user", text: msg, attachments: refs.length ? refs : undefined }]);
    const content = refs.length
      ? [...(msg ? [{ type: "text", text: msg }] : []), ...refs.map((r) => ({ type: "fa_upload", ...r }))]
      : msg;
    const next = [...transcript, { role: "user", content }];
    setTranscript(next);
    await call({ messages: next, context: pageContext(location.pathname, selectedEntity, selectedScope) }, next.length);
  };

  const resolve = async (approve) => {
    if (!pending || busy) return;
    const p = pending;
    setPending(null);
    addItems([{ kind: "user", text: approve ? "Confirm" : "Cancel", small: true }]);
    await call({
      messages: transcript,
      context: pageContext(location.pathname, selectedEntity, selectedScope),
      resolve: { action_id: p.action_id, approve },
    }, transcript.length);
  };

  const reset = () => {
    if (busy) return;
    if (dirtyRef.current && items.length) {
      // Save the chat being left before its pending debounced save is dropped.
      dirtyRef.current = false;
      api.put(`/assistant/conversations/${conversationId}`, { transcript, items, pending })
        .catch((e) => console.warn("assistant: could not save conversation", e?.message));
    }
    setConversationId(newConversationId());
    setTitle("");
    setTranscript([]);
    setItems([]);
    setPending(null);
    setAttachments([]);
    setView("chat");
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const openConversation = async (id) => {
    if (busy) return;
    if (id === conversationId) { setView("chat"); return; }
    try {
      const res = await api.get(`/assistant/conversations/${id}`);
      const c = res.data || {};
      setConversationId(c.conversation_id || id);
      setTitle(c.title || "");
      setTranscript(c.transcript || []);
      setItems(c.items || []);
      setPending(c.pending || null);
      dirtyRef.current = false;
      lastActiveRef.current = Date.now();
      setView("chat");
    } catch (e) {
      addItems([{ kind: "error", text: "Could not open that conversation" }]);
      setView("chat");
    }
  };

  // Reopening after 2h idle starts a fresh chat.
  useEffect(() => {
    if (!open || busy || items.length === 0) return;
    if (Date.now() - (lastActiveRef.current || 0) > IDLE_NEW_CHAT_MS) reset();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const onDeleted = (id) => { if (id === conversationId) reset(); };
  const onRenamed = (id, t) => { if (id === conversationId) setTitle(t); };

  const download = async (f) => {
    try {
      const path = f.path.replace(/^\/api/, "");
      const res = await api.get(path, { responseType: "blob", timeout: 120000 });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers?.["content-disposition"] || "";
      const name = (cd.match(/filename="?([^";]+)"?/) || [])[1] || `${f.label}.pdf`;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      addItems([{ kind: "error", text: `Could not download ${f.label}` }]);
    }
  };

  const onLinkClick = (e) => {
    const a = e.target.closest("a[data-internal]");
    if (a) {
      e.preventDefault();
      navigate(a.getAttribute("data-internal"));
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{ bottom: "calc(1.25rem + env(safe-area-inset-bottom, 0px))" }}
        className="fixed right-5 z-40 flex items-center gap-2 rounded-full bg-primary text-primary-foreground shadow-lg px-4 py-3 text-sm font-semibold hover:opacity-90 transition"
        title="Assistant (Ctrl+J)"
        data-testid="assistant-open"
      >
        <Sparkles className="w-4 h-4" />
        Chat
        {pending && <span className="w-2 h-2 rounded-full bg-amber-400" />}
      </button>
    );
  }

  return (
    <div
      className={`fixed inset-y-0 right-0 z-50 w-full sm:w-[440px] h-[100dvh] bg-card border-l shadow-2xl flex flex-col ${dragOver ? "border-primary ring-2 ring-primary/40" : "border-border"}`}
      style={{ paddingTop: "env(safe-area-inset-top, 0px)", paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      data-testid="assistant-panel"
      onDragOver={(e) => {
        if (view !== "chat" || !Array.from(e.dataTransfer?.types || []).includes("Files")) return;
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); }}
      onDrop={(e) => {
        if (view !== "chat" || !e.dataTransfer?.files?.length) return;
        e.preventDefault();
        setDragOver(false);
        addFiles(e.dataTransfer.files);
      }}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2 min-w-0">
          {view === "history" ? (
            <button type="button" onClick={() => setView("chat")} className="p-2 sm:p-1 -ml-1 rounded hover:bg-muted text-muted-foreground" title="Back to chat">
              <ArrowLeft className="w-4 h-4" />
            </button>
          ) : (
            <Sparkles className="w-4 h-4 text-primary shrink-0" />
          )}
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">
              {view === "history" ? "Chats" : (items.length && title) ? title : "Chat"}
            </p>
            {view === "chat" && <p className="text-[11px] text-muted-foreground">reads everything, changes only on Confirm</p>}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={() => setView((v) => (v === "history" ? "chat" : "history"))}
            className={`p-2.5 sm:p-1.5 rounded hover:bg-muted ${view === "history" ? "bg-muted text-foreground" : "text-muted-foreground"}`}
            title="Past chats"
            data-testid="assistant-history"
          >
            <History className="w-4 h-4" />
          </button>
          <button type="button" onClick={reset} disabled={busy} className="p-2.5 sm:p-1.5 rounded hover:bg-muted text-muted-foreground disabled:opacity-40" title="New chat" data-testid="assistant-new">
            <SquarePen className="w-4 h-4" />
          </button>
          <button type="button" onClick={() => setOpen(false)} className="p-2.5 sm:p-1.5 rounded hover:bg-muted text-muted-foreground" title="Close (Ctrl+J)" aria-label="Close" data-testid="assistant-close">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {view === "history" ? (
        <ConversationList
          currentId={conversationId}
          busy={busy}
          onOpen={openConversation}
          onNew={reset}
          onDeleted={onDeleted}
          onRenamed={onRenamed}
        />
      ) : (
      <>
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" onClick={onLinkClick}>
        {items.length === 0 && (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Chat about any client, booking, quote or invoice, or tell me what to do. I show every change for you to confirm first.
            </p>
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => (s.includes("<") ? setInput(s.replace(/<[^>]+>/, "")) : send(s))}
                className="block w-full text-left text-sm rounded-lg border border-border px-3 py-2 hover:bg-muted/60"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {items.map((it, i) => <Item key={i} it={it} onDownload={download} />)}

        {pending && (
          <div className={`rounded-lg border p-3 ${pending.irreversible ? "border-rose-300 bg-rose-50/60" : "border-amber-300 bg-amber-50/60"}`} data-testid="assistant-pending">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">Needs your confirmation</p>
            <p className="text-sm font-medium text-foreground">{pending.summary}</p>
            {pending.irreversible && (
              <p className="flex items-center gap-1 text-xs text-rose-700 mt-1">
                <AlertTriangle className="w-3.5 h-3.5" />This cannot be undone.
              </p>
            )}
            <PendingDetail p={pending} />
            <div className="flex gap-2 mt-3">
              <button
                type="button"
                onClick={() => resolve(true)}
                disabled={busy}
                className={`flex items-center gap-1 text-sm font-semibold px-3 py-1.5 rounded-md text-white ${pending.irreversible ? "bg-rose-600 hover:bg-rose-700" : "bg-primary hover:opacity-90"}`}
                data-testid="assistant-confirm"
              >
                <Check className="w-4 h-4" />Confirm
              </button>
              <button
                type="button"
                onClick={() => resolve(false)}
                disabled={busy}
                className="text-sm px-3 py-1.5 rounded-md border border-border hover:bg-muted"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {busy && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />Working on it
          </div>
        )}
      </div>

      <div className="border-t border-border p-3">
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2" data-testid="assistant-attachments">
            {attachments.map((a) => (
              <div key={a.key} className="relative group">
                {a.previewUrl ? (
                  <img src={a.previewUrl} alt={a.file.name} className="h-14 w-14 object-cover rounded-md border border-border" />
                ) : (
                  <div className="h-14 w-24 rounded-md border border-border bg-muted/50 flex flex-col items-center justify-center px-1">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    <span className="text-[10px] text-muted-foreground truncate w-full text-center">{a.file.name}</span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removeAttachment(a.key)}
                  disabled={busy}
                  className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-foreground text-background flex items-center justify-center"
                  title={`Remove ${a.file.name}`}
                  aria-label={`Remove ${a.file.name}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-end gap-2">
          <input
            ref={fileRef}
            type="file"
            multiple
            accept={ACCEPT}
            className="hidden"
            data-testid="assistant-file-input"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={busy || attachments.length >= MAX_FILES}
            className="h-9 w-9 flex items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-muted disabled:opacity-40"
            title="Attach pictures or files"
            aria-label="Attach pictures or files"
            data-testid="assistant-attach"
          >
            <Paperclip className="w-4 h-4" />
          </button>
          <textarea
            ref={inputRef}
            rows={2}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onPaste={onPaste}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder={pending ? "Confirm above, or type to change the plan" : "Chat or tell me what to do"}
            className="flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-base sm:text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            data-testid="assistant-input"
          />
          <button
            type="button"
            onClick={() => send()}
            disabled={busy || (!input.trim() && attachments.length === 0)}
            className="h-9 w-9 flex items-center justify-center rounded-md bg-primary text-primary-foreground disabled:opacity-40"
            title="Send"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[10px] text-muted-foreground mt-1">Page: {location.pathname}</p>
      </div>
      </>
      )}
    </div>
  );
}

function dayGroup(iso) {
  if (!iso) return "Older";
  const d = new Date(iso);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.floor((today - new Date(d.getFullYear(), d.getMonth(), d.getDate())) / 86400000);
  if (diff <= 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff < 7) return "Previous 7 days";
  if (diff < 30) return "Previous 30 days";
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

function shortWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay
    ? d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

function ConversationList({ currentId, busy, onOpen, onNew, onDeleted, onRenamed }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");
  const searchRef = useRef(null);

  useEffect(() => { searchRef.current?.focus(); }, []);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await api.get("/assistant/conversations", { params: q.trim() ? { q: q.trim() } : {} });
        if (!cancelled) { setRows(res.data?.conversations || []); setError(null); }
      } catch (e) {
        if (!cancelled) setError("Could not load your chats");
      }
    }, q ? 250 : 0);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/assistant/conversations/${c.conversation_id}`);
      setRows((prev) => (prev || []).filter((r) => r.conversation_id !== c.conversation_id));
      onDeleted(c.conversation_id);
    } catch {
      setError("Could not delete that chat");
    }
  };

  const saveTitle = async (c) => {
    const t = draft.trim();
    setEditing(null);
    if (!t || t === c.title) return;
    try {
      await api.patch(`/assistant/conversations/${c.conversation_id}`, { title: t });
      setRows((prev) => (prev || []).map((r) => (r.conversation_id === c.conversation_id ? { ...r, title: t } : r)));
      onRenamed(c.conversation_id, t);
    } catch {
      setError("Could not rename that chat");
    }
  };

  const groups = [];
  (rows || []).forEach((c) => {
    const g = dayGroup(c.updated_at);
    const last = groups[groups.length - 1];
    if (last && last.label === g) last.rows.push(c);
    else groups.push({ label: g, rows: [c] });
  });

  return (
    <div className="flex-1 flex flex-col min-h-0" data-testid="assistant-conversations">
      <div className="px-4 pt-3 pb-2 space-y-2 border-b border-border">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            ref={searchRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search your chats"
            className="w-full rounded-md border border-input bg-background pl-8 pr-3 py-2 text-base sm:text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            data-testid="assistant-search"
          />
        </div>
        <button
          type="button"
          onClick={onNew}
          disabled={busy}
          className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm font-medium hover:bg-muted disabled:opacity-40"
        >
          <SquarePen className="w-4 h-4" />New chat
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-2 py-2">
        {error && <p className="px-2 py-2 text-xs text-rose-700">{error}</p>}
        {rows === null && !error && (
          <p className="flex items-center gap-2 px-2 py-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" />Loading</p>
        )}
        {rows && rows.length === 0 && (
          <p className="px-2 py-2 text-sm text-muted-foreground">{q.trim() ? `No chats match "${q.trim()}"` : "No saved chats yet"}</p>
        )}
        {groups.map((g) => (
          <div key={g.label} className="mb-2">
            <p className="px-2 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{g.label}</p>
            {g.rows.map((c) => (
              <div
                key={c.conversation_id}
                className={`group flex items-start gap-1 rounded-md px-2 py-1.5 cursor-pointer ${c.conversation_id === currentId ? "bg-muted" : "hover:bg-muted/60"}`}
                onClick={() => editing !== c.conversation_id && onOpen(c.conversation_id)}
                data-testid={`assistant-conv-${c.conversation_id}`}
              >
                <div className="flex-1 min-w-0">
                  {editing === c.conversation_id ? (
                    <input
                      autoFocus
                      value={draft}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setDraft(e.target.value)}
                      onBlur={() => saveTitle(c)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveTitle(c);
                        if (e.key === "Escape") setEditing(null);
                      }}
                      className="w-full rounded border border-input bg-background px-1.5 py-0.5 text-base sm:text-sm"
                    />
                  ) : (
                    <p className="text-sm text-foreground truncate">
                      {c.title || "New chat"}
                      {c.has_pending && <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-amber-400 align-middle" title="Waiting for Confirm" />}
                    </p>
                  )}
                  {c.snippet && <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{c.snippet}</p>}
                </div>
                <span className="text-[11px] text-muted-foreground whitespace-nowrap pt-0.5 hidden [@media(hover:hover)]:inline [@media(hover:hover)]:group-hover:hidden">{shortWhen(c.updated_at)}</span>
                <div className="flex [@media(hover:hover)]:hidden [@media(hover:hover)]:group-hover:flex items-center">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setEditing(c.conversation_id); setDraft(c.title || ""); }}
                    className="p-2 [@media(hover:hover)]:p-1 rounded hover:bg-background text-muted-foreground"
                    title="Rename"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); remove(c); }}
                    className="p-2 [@media(hover:hover)]:p-1 rounded hover:bg-background text-muted-foreground hover:text-rose-600"
                    title="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function PendingDetail({ p }) {
  const [openDetail, setOpenDetail] = useState(false);
  const hasPreview = Array.isArray(p.preview) && p.preview.length > 0;
  return (
    <div className="mt-2">
      {Array.isArray(p.files) && p.files.length > 0 && (
        <p className="text-xs text-foreground/80 mb-1">
          <span className="text-muted-foreground">Files</span> {p.files.join(", ")}
        </p>
      )}
      {hasPreview && (
        <div className="space-y-1.5 mb-1">
          {p.preview.map((e, i) => (
            <div key={i} className="text-xs text-foreground/80 bg-card rounded border border-border px-2 py-1.5">
              {e.error ? <span className="text-rose-600">{e.error}</span> : (
                <>
                  <p><span className="text-muted-foreground">To</span> {e.to}{e.cc ? <><span className="text-muted-foreground"> Cc</span> {e.cc}</> : null}</p>
                  <p className="truncate"><span className="text-muted-foreground">Subject</span> {e.subject}</p>
                  <p><span className="text-muted-foreground">Invoices</span> {(e.invoices || []).join(", ")}</p>
                </>
              )}
            </div>
          ))}
        </div>
      )}
      {p.method && (
        <button type="button" onClick={() => setOpenDetail((v) => !v)} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
          {openDetail ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          {p.method} {p.path}
        </button>
      )}
      {openDetail && (p.body || p.query) && (
        <pre className="mt-1 text-[11px] bg-card border border-border rounded p-2 overflow-x-auto max-h-48">
          {JSON.stringify({ query: p.query || undefined, body: p.body || undefined }, null, 2)}
        </pre>
      )}
    </div>
  );
}

function Item({ it, onDownload }) {
  const [openSteps, setOpenSteps] = useState(false);
  if (it.kind === "user") {
    return (
      <div className="flex flex-col items-end gap-1">
        {Array.isArray(it.attachments) && it.attachments.length > 0 && (
          <div className="flex flex-wrap justify-end gap-1.5 max-w-[85%]">
            {it.attachments.map((a) => <AttachmentThumb key={a.upload_id} a={a} />)}
          </div>
        )}
        {it.text ? (
          <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${it.small ? "bg-muted text-muted-foreground text-xs" : "bg-primary text-primary-foreground"}`}>
            {it.text}
          </div>
        ) : null}
      </div>
    );
  }
  if (it.kind === "assistant") {
    return (
      <div
        className="assistant-md text-sm text-foreground leading-relaxed [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2 [&_a]:text-blue-600 [&_a]:underline-offset-2 hover:[&_a]:underline [&_code]:text-xs [&_code]:bg-muted [&_code]:px-1 [&_code]:rounded"
        dangerouslySetInnerHTML={{ __html: renderMarkdown(it.text) }}
      />
    );
  }
  if (it.kind === "steps") {
    const labels = [...new Set(it.steps.map((s) => TOOL_LABELS[s.tool] || s.tool))];
    return (
      <div className="text-xs text-muted-foreground">
        <button type="button" onClick={() => setOpenSteps((v) => !v)} className="flex items-center gap-1 hover:text-foreground">
          {openSteps ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          {it.steps.length} step{it.steps.length === 1 ? "" : "s"}: {labels.join(", ")}
        </button>
        {openSteps && (
          <ul className="mt-1 space-y-0.5 pl-4">
            {it.steps.map((s, i) => (
              <li key={i} className="font-mono text-[10px] break-all">
                {s.tool} {s.input?.method || ""} {s.input?.path || s.input?.query || s.input?.client || (s.input?.invoice_ids || []).join(",")}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }
  if (it.kind === "executed") {
    return (
      <div className={`text-xs rounded-md px-3 py-2 border ${it.cancelled ? "border-border text-muted-foreground" : it.ok ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-rose-200 bg-rose-50 text-rose-800"}`}>
        {it.cancelled ? "Cancelled: " : it.ok ? "Done: " : `Failed (HTTP ${it.status}): `}{it.summary}
      </div>
    );
  }
  if (it.kind === "files") {
    return (
      <div className="flex flex-wrap gap-2">
        {it.files.map((f, i) => (
          <button key={i} type="button" onClick={() => onDownload(f)} className="flex items-center gap-1.5 text-xs font-medium rounded-md border border-border px-2.5 py-1.5 hover:bg-muted">
            <Download className="w-3.5 h-3.5" />{f.label}
          </button>
        ))}
      </div>
    );
  }
  if (it.kind === "error") {
    return <div className="text-xs rounded-md px-3 py-2 border border-rose-200 bg-rose-50 text-rose-800">{it.text}</div>;
  }
  return null;
}

function AttachmentThumb({ a }) {
  const isImage = (a.content_type || "").startsWith("image/");
  const [url, setUrl] = useState(() => thumbCache.get(a.upload_id) || null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (url || !isImage) return undefined;
    let alive = true;
    api.get(`/assistant/uploads/${a.upload_id}`, { responseType: "blob", timeout: 60000 })
      .then((res) => {
        const u = URL.createObjectURL(res.data);
        thumbCache.set(a.upload_id, u);
        if (alive) setUrl(u);
      })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [a.upload_id, isImage, url]);

  const open = async () => {
    try {
      let u = thumbCache.get(a.upload_id);
      if (!u) {
        const res = await api.get(`/assistant/uploads/${a.upload_id}`, { responseType: "blob", timeout: 120000 });
        u = URL.createObjectURL(res.data);
        thumbCache.set(a.upload_id, u);
      }
      window.open(u, "_blank", "noopener");
    } catch {
      setFailed(true);
    }
  };

  if (isImage && url && !failed) {
    return (
      <button type="button" onClick={open} title={a.filename} className="block">
        <img src={url} alt={a.filename} className="h-20 w-20 object-cover rounded-md border border-border" />
      </button>
    );
  }
  return (
    <button type="button" onClick={open} title={a.filename}
      className="flex items-center gap-1.5 max-w-[220px] text-xs rounded-md border border-border bg-muted/40 px-2 py-1.5 hover:bg-muted">
      <FileText className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
      <span className="truncate">{a.filename}</span>
    </button>
  );
}
